<template>
    <div class="vrm-modal vrm-modal--wide mfp-with-anim">
        <div class="vrm-modal__header">
            <div class="vrm-modal__title">
                {{ $t('teams.edit_team', { i18nFallback: 'Edit team' }) }}
            </div>
        </div>
        <form class="vrm-modal__content" @submit.prevent="onSubmit" @reset.prevent="onReset">
            <fieldset class="vrm-fieldset">
                <div class="vrm-form__fieldset-item">
                    <vrm-text-field
                        v-model="name"
                        class="sg-element"
                        type="text"
                        required
                        :disabled="busy"
                        :title="$t('teams.name', { i18nFallback: 'Name' })"
                    ></vrm-text-field>
                </div>
                <div class="vrm-form__fieldset-item">
                    <label class="sg-textfield-label">
                        <span class="sg-textfield-label-caption">
                            {{ $t('teams.owner', { i18nFallback: 'Owner' }) }}
                        </span>
                    </label>
                    <vrm-dropdown v-model="owner" :options="members" :disabled="!isOwner" />
                </div>
                <div class="sg-spread">
                    <button type="reset" class="sg-btn sg-site" :disabled="busy">
                        {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                    </button>
                    <button type="submit" class="sg-btn sg-blue" :disabled="busy">
                        {{ $t('common.save', { i18nFallback: 'Save' }) }}
                    </button>
                </div>
            </fieldset>
        </form>
        <modal-close-handle />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import { ngInject } from '@/vue/decorators/angular-interop';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: {
        VrmDropdown,
        ModalCloseHandle,
        VrmTextField,
    },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('general')
@AngularLocaleBundle('teams')
export default class UserGroupSettingsModal extends Vue {
    @Prop({ type: Object })
    team;

    @Prop({ type: Number })
    idUser;

    /** @type UserGroupsService */
    @ngInject()
    UserGroupsService;
    @ngInject()
    UserService;
    @ngInject()
    VrmModalService;
    @ngInject()
    $rootScope;

    @Watch('team', { immediate: true })
    onTeamChanged(team) {
        if (team) {
            this.name = team.name;
            this.idOwner = team.idOwner;
        } else {
            this.name = '';
            this.idOwner = 0;
        }
    }

    @Watch('lastItem', { immediate: true })
    onLastItemChanged(value) {
        //this is true only when the modal initially opens
        if (this.admins.length === 0) {
            /* for some reason admins cannot be initialized as [...this.team.admins]. To get around this, we have
               to initialize it as [] and give it the already existing admins here */
            this.admins = [...this.team.admins];
            this.admins.push({ name: '', idUser: null });
            return;
        }

        if (value && this.lastItem.idUser) {
            this.admins.push({ name: '', idUser: null });
        }
    }

    @Watch('idOwner', { immediate: true })
    onOwnerChanged(value) {
        /* an owner cannot also be an admin, so when an admin is made into an owner, remove
           him from the list of admins */
        for (let index = 0; index < this.admins.length - 1; index++) {
            if (this.admins[index].idUser == value) {
                this.admins.splice(index, 1);
                break;
            }
        }
    }

    name = '';
    idOwner = 0;
    busy = false;
    admins = [];

    get lastItem() {
        if (this.admins.length === 0) return null;
        return this.admins[this.admins.length - 1];
    }

    get validAdminChoices() {
        return this.members.filter(
            item => !this.admins.includes(item) && item.idUser != this.idOwner,
        );
    }

    get members() {
        return this.team ? this.team.members : [];
    }

    get isOwner() {
        return this.team.idOwner === this.idUser || this.$rootScope.userState.isFullAdmin;
    }

    get owner() {
        return this.team.members.find(m => m.idUser === this.idOwner);
    }

    set owner(value) {
        this.idOwner = value ? value.idUser : 0;
    }

    removeAdminRow(index) {
        this.admins.splice(index, 1);
    }

    get adminIds() {
        //process admins and get their user ids
        let pAdmins = [];
        //last element is always just a placeholder to allow the user to add an additional admin, so ignore it
        for (let index = 0; index < this.admins.length - 1; index++) {
            pAdmins.push(this.admins[index].idUser);
        }
        return pAdmins;
    }

    async onSubmit() {
        if (this.busy) return;
        this.busy = true;
        try {
            await this.UserGroupsService.updateUserGroup(this.team.idUserGroup, {
                name: this.name,
                idOwner: this.idOwner > 0 ? this.idOwner : undefined,
                admins: this.adminIds,
            });
            this.$emit('close');
        } finally {
            this.busy = false;
        }
    }

    onReset() {
        if (this.busy) return;
        this.$emit('close');
    }
}
</script>
